/**
 * F17 (Wave 2C) — admin booking API client.
 *
 * Endpoints:
 *   GET  /admin/bookings                      → listAdminBookings (read-only list)
 *   GET  /admin/bookings/:id                  → adminBookings.findOne
 *   POST /admin/bookings/:id/force-transition → adminBookings.forceTransition
 *
 * The route is read-only at MVP except for the super-admin force-transition
 * action; everything else stays in the venue-staff surface.
 */
import type {
  BookingDetail,
  BookingStatus,
  BookingSummary,
  ForceTransitionInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient.js';

interface DataEnvelope<T> {
  readonly data: T;
}

/** Filters accepted by `GET /admin/bookings`. */
export interface AdminBookingsListOpts {
  /** Scope to a single venue (the venue-detail read-through passes this). */
  readonly venueId?: string;
  /** Single lifecycle status — sent as the CSV `status` query param. */
  readonly status?: BookingStatus;
  /** Inclusive lower bound on the slot start date, `YYYY-MM-DD`. */
  readonly dateFrom?: string;
  /** Inclusive upper bound on the slot start date, `YYYY-MM-DD`. */
  readonly dateTo?: string;
  /** Page size (server caps at 100; default 20). */
  readonly limit?: number;
}

export interface AdminBookingsListResponse {
  readonly data: readonly BookingSummary[];
  readonly nextCursor: string | null;
}

/**
 * GET /admin/bookings — read-only, audited list of bookings, most-recent-first
 * (by slot start). Filterable by venue / status / start-date range. Returns the
 * lightweight `BookingSummary` row shape; each row deep-links to the F17 detail
 * page at `/bookings/:id`.
 */
export async function listAdminBookings(
  opts: AdminBookingsListOpts = {},
): Promise<AdminBookingsListResponse> {
  return adminFetch<AdminBookingsListResponse>(
    `/admin/bookings${buildQuery({
      venueId: opts.venueId,
      status: opts.status,
      dateFrom: opts.dateFrom,
      dateTo: opts.dateTo,
      limit: opts.limit,
    })}`,
  );
}

/**
 * GET /admin/bookings/:id
 *
 * Returns `null` on 404 so the caller can render the not-found surface
 * without catching an exception.
 */
export async function getAdminBooking(id: string): Promise<BookingDetail | null> {
  try {
    const body = await adminFetch<DataEnvelope<BookingDetail>>(
      `/admin/bookings/${encodeURIComponent(id)}`,
    );
    return body.data;
  } catch (err) {
    if (
      err !== null &&
      typeof err === 'object' &&
      'status' in err &&
      (err as { status: number }).status === 404
    ) {
      return null;
    }
    throw err;
  }
}

/**
 * POST /admin/bookings/:id/force-transition (super_admin only)
 *
 * Bypasses the booking state-machine guard for incident response. The body
 * carries the `target` (`'lifecycle' | 'payment'`), the destination `to`
 * value (which the server validates against the chosen target's enum), and
 * a mandatory `reason` audit-logged via F06's `onSend` hook.
 */
export async function forceTransitionAdminBooking(
  id: string,
  input: ForceTransitionInput,
): Promise<BookingDetail> {
  const body = await adminFetch<DataEnvelope<BookingDetail>>(
    `/admin/bookings/${encodeURIComponent(id)}/force-transition`,
    {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    },
  );
  return body.data;
}
